import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import _ from 'lodash'

import { smartChat } from 'api'
import { Button } from 'components/Form'
import { DotsOptionsSelector } from 'components/common'
import ModalWrapper from 'components/ModalWrapper'
import ModalDelete from 'components/ModalDelete'
import MessageType from './MessageType'
import Message from './Message'
import Nixi1Spinner from 'components/Nixi1Spinner'

const Messages = () => {
  const { processesList, company } = useSelector(store => ({
    processesList: store.smartchatHeader.processesList,
    company: store.companies
  }), shallowEqual)
  const [messageGroup, setMessageGroup] = useState({})
  const [groupSelected, setGroupSelected] = useState('')
  const [optionsSelectorIsOpen, setOptionsSelectorIsOpen] = useState({})
  const [isOpenMessageType, setIsOpenMessageType] = useState(false)
  const [isOpenDelete, setIsOpenDelete] = useState(false)
  const [isOpenInfoModal, setIsOpenInfoModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) getMessageGroup()

    return () => isSubscribed = false
  }, [])

  const toggleOptionsSelector = (type) => {
    if (!type) return setOptionsSelectorIsOpen({})

    !optionsSelectorIsOpen[type]
      ? setOptionsSelectorIsOpen(state => ({ ...state, [type]: true }))
      : setOptionsSelectorIsOpen(state => ({ ...state, [type]: false }))

    setGroupSelected(type)
  }

  const onToggleMessageType = () => {
    setIsOpenMessageType(!isOpenMessageType)
    toggleOptionsSelector()
    if (isOpenMessageType) setGroupSelected('')
  }
  const toggleDelete = () => {
    setIsOpenDelete(!isOpenDelete)
    toggleOptionsSelector()
    if (isOpenDelete) setGroupSelected('')
  }

  const toggleInfoModal = () => setIsOpenInfoModal(!isOpenInfoModal)

  const getMessageGroup = async ({ group, message, mode } = {}) => {
    if (group) {
      const _messageGroup = _.cloneDeep(messageGroup)
      if (mode === 'add') {
        _messageGroup.groupsInfo.groups.push({ ...group })
      }
      else if (mode === 'edit') {
        _messageGroup.groupsInfo.groups.forEach(it => {
          if (it._id === group._id) {
            it.name = group.name
          }
        })
      }
      else if (mode === 'remove') {
        _messageGroup.groupsInfo.groups.forEach((it, idx) => {
          if (it._id === group._id) {
            _messageGroup.groupsInfo.groups.splice(idx, 1)
          }
        })
      }
      setMessageGroup(_messageGroup)
      return
    }

    if (message) {
      const _messageGroup = _.cloneDeep(messageGroup)
      if (mode === 'add') {
        _messageGroup.messages.forEach((it, idx) => {
          if (it.createdTo === message.createdTo) {
            if (it._id === '0')
              _messageGroup.messages.splice(idx, 1)
          }
        })
        _messageGroup.messages.push({ ...message })
        _messageGroup.groupsInfo.groups.forEach(it => {
          if (it._id === message.createdTo) {
            it.messages.push(message._id)
          }
        })
      }
      else if (mode === 'edit') {
        _messageGroup.messages.forEach(it => {
          if (it.createdTo === message.createdTo) {
            if (it._id === message._id) {
              it.name = message.name
              it.message = message.message
            }
          }
        })
      }
      else if (mode === 'remove') {
        _messageGroup.messages.forEach((it, idx) => {
          if (it.createdTo === message.createdTo) {
            if (it._id === '0' || it._id === message._id)
              _messageGroup.messages.splice(idx, 1)
          }
        })
      }
      setMessageGroup(_messageGroup)
      return
    }
    const groupsResult = await smartChat.getMessageGroup()
    const _messageGroup = _.cloneDeep(groupsResult.data)

    // ORDENAMIENTO DE GRUPOS
    _messageGroup.groupsInfo.groups = orderByList(_messageGroup.groupsInfo.groups, _messageGroup.groupsInfo.groupsOrder) // groupsSort

    let messageOrder = []
    _messageGroup.groupsInfo.groups.forEach(it => {
      messageOrder = messageOrder.concat(it.messages)
    })
    _messageGroup.messages = orderByList(_messageGroup.messages, messageOrder)

    setMessageGroup(_messageGroup)
    setLoading(false)
    toggleOptionsSelector()  // HIDE EDIT MENU
  }

  const deleteGroup = async (groupData) => {
    setIsSubmitting(true)

    const clear = () => {
      setIsSubmitting(false)
      toggleDelete()
      toggleOptionsSelector()
    }

    for (const process of processesList) {
      const { data } = await smartChat.getProcessDetail(process._id)

      // Al entrar en el proceso
      for (const action of data.onNewProcess.actionList) {
        if (action.eventData.messageId === groupData._id && groupData.messages.length) {
          clear()
          toggleInfoModal()
          return
        }
      }

      // En el proceso
      for (const onProcess of data.onProcess) {
        for (const action of onProcess.actionList) {
          for (const botAction of action.botActionList) {
            if (botAction.eventData.messageId === groupData._id && groupData.messages.length) {
              clear()
              toggleInfoModal()
              return
            }
          }
        }
      }

      // Al salir del proceso
      for (const action of data.onLeaveProcess.actionList) {
        if (action.eventData.messageId === groupData._id && groupData.messages.length) {
          clear()
          toggleInfoModal()
          return
        }
      }
    }

    smartChat.deleteGroup({ groupMessagesId: messageGroup.groupsInfo._id, groupId: groupData._id }).then(() => {
      clear()
      getMessageGroup({ group: groupData, mode: 'remove' })
    }).catch(() => {
      setIsSubmitting(false)
    })
  }

  const addMessage = ({ mode, group } = {}) => {
    const _messageGroup = _.cloneDeep(messageGroup)
    if (mode === 'add') {
      _messageGroup.messages.push({ _id: '0', name: '', createdTo: group._id, message: { contentHtml: '', attachments: [], buttons: [] } })
    }
    setMessageGroup(_messageGroup)
  }

  const onDragEnd = (result) => {
    if (!result.destination) {
      return
    }
    const _messageGroup = _.cloneDeep(messageGroup)
    const _messageGroupTemp = _.cloneDeep(messageGroup)

    // ARRASTRE DE TIPOS
    if (result.destination.droppableId === 'type-list') {
      let _groupSource = _messageGroup.groupsInfo.groups[result.source.index]
      let groupsOrder = []
      // REMUEVO EL ITEM QUE ARRASTRÉ
      _messageGroup.groupsInfo.groups.splice(result.source.index, 1)

      // AGREGO EN DESTINO
      _messageGroup.groupsInfo.groups.splice(result.destination.index, 0, _groupSource)

      // ORDENAMIENTO
      _messageGroup.groupsInfo.groups.forEach(it => {
        groupsOrder.push(it._id)
      })

      _messageGroup.groupsInfo.groupsOrder = groupsOrder

      // REALIZO EL RENDER
      setMessageGroup(_messageGroup)

      // UPDATE A LA BASE DE DATOS
      const data = {
        groupMessagesId: _messageGroup.groupsInfo._id,
        groupsOrder: groupsOrder
      }

      smartChat.groupOrder(data).then(() => {
      }).catch(() => {
        setMessageGroup(_messageGroupTemp)
      })
    }
    else if (result.type === 'MESSAGES') {
      const droppableIdDestination = result.destination.droppableId
      const droppableIdSource = result.source.droppableId
      let newMessageListDestination = _messageGroup.groupsInfo.groups.find(it => it._id === droppableIdDestination).messages
      let newMessageListSource = _messageGroup.groupsInfo.groups.find(it => it._id === droppableIdSource).messages

      // REMUEVO EL ITEM QUE ARRASTRÉ Y REVISO QUE SEA DEL MISMO TIPO DE OTRO
      if (droppableIdDestination === droppableIdSource)
        newMessageListDestination.splice(result.source.index, 1)
      else
        newMessageListSource.splice(result.source.index, 1)

      // AGREGO EN DESTINO
      newMessageListDestination.splice(result.destination.index, 0, result.draggableId)

      // ACTUALIZO LOS TYPOS Y LISTA DE MENSAJES
      _messageGroup.groupsInfo.groups.forEach(it => {
        if (it._id === droppableIdDestination) {
          it.messages = newMessageListDestination
        }
        if (droppableIdDestination !== droppableIdSource) {
          if (it._id === droppableIdSource) {
            it.messages = newMessageListSource
          }
        }
      })

      // ACTUALIZO EL TIPO EN EL MENSAJE
      if (droppableIdDestination !== droppableIdSource) {
        _messageGroup.messages.forEach(it => {
          if (it._id === result.draggableId) {
            it.createdTo = droppableIdDestination
          }
        })
      }

      // ORDENAMIENTO
      let sorting = _.clone(newMessageListDestination)  // [1,2,3]
      const resultSort = _messageGroup.messages.map(function(item) {
        var n = sorting.indexOf(item._id)
        sorting[n] = ''
        return [n, item]
      }).sort().map(function(j) { return j[1] })

      _messageGroup.messages = resultSort

      // REALIZO EL RENDER
      setMessageGroup(_messageGroup)

      // UPDATE A LA BASE DE DATOS
      // https://d.dev.v2.nixi1.dkv.es/api-docs/#/Chatbot/put_chatbot_messages_groups_order_messages
      let data = {}
      if (droppableIdDestination === droppableIdSource) {
        data = {
          action: "messagesOrdered",
          groupMessagesId: _messageGroup.groupsInfo._id,
          groupId: droppableIdSource,
          messages: newMessageListSource
        }
      }
      else {
        data = {
          action: "messageMoved",
          groupMessagesId: _messageGroup.groupsInfo._id,
          messageId: result.draggableId,
          from: {
            groupId: droppableIdSource,
            messages: newMessageListSource
          },
          to: {
            groupId: droppableIdDestination,
            messages: newMessageListDestination
          }
        }
      }

      smartChat.messageOrder(data).then(() => {
      }).catch(() => {
        setMessageGroup(_messageGroupTemp)
      })
    }
  }

  const orderByList = (arrayRaw, listToOrder) => {
    const arraySort = arrayRaw.map(function(item) {
      var n = listToOrder.indexOf(item._id)
      listToOrder[n] = ''
      return [n, item]
    }).sort().map(function(j) { return j[1] })
    return arraySort
  }

  if (loading || !processesList) return <div className='containerSpinner' style={{ height:'100%' }}> <Nixi1Spinner height='15vh' /> </div>  

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="type-list" type="COLUMN">
        {(provided_type, snapshot_type) => (
          <div
            className='messages__container animated fadeIn'
            ref={provided_type.innerRef}
            {...provided_type.droppableProps}
            style={{ minHeight: 20, backgroundColor: snapshot_type.isDraggingOver && '#e2e2e2' }}
          >
            {
              messageGroup.groupsInfo.groups.map((group, index) => (
                <Draggable
                  key={`group-${group._id}`}
                  draggableId={group._id}
                  index={index}
                >
                  {(provided_type2) => {
                    return (
                      <div
                        key={group._id}
                        ref={provided_type2.innerRef}
                        {...provided_type2.draggableProps}
                        className={'group-wrapper'}
                      >
                        <div className={'group-header'}>
                          <span className='group-name'>{group.name}</span>
                          <div className='icons'>
                            <div className='group-options'>
                              <DotsOptionsSelector
                                isOpen={optionsSelectorIsOpen[group._id]}
                                toggle={() => toggleOptionsSelector(group._id)}
                                options={[
                                  {
                                    name: I18n.t('dashboard_edit'),
                                    onClick: onToggleMessageType
                                  },
                                  {
                                    name: I18n.t('dashboard_delete'),
                                    onClick: toggleDelete
                                  }
                                ]}
                                position='top'
                              />
                            </div>
                            <i {...provided_type2.dragHandleProps} className={`fa fa-bars drag-item`} />
                          </div>
                        </div>
                        <div className={'group-body'}>
                          <Droppable
                            key={`group-${group._id}`}
                            droppableId={group._id}
                            type={'MESSAGES'}
                          >
                            {(provided_messages, snapshot_messages) => (
                              <div
                                ref={provided_messages.innerRef}
                                {...provided_messages.droppableProps}
                                style={{ minHeight: 20, backgroundColor: snapshot_messages.isDraggingOver && '#e2e2e2' }}
                              >
                                {
                                  messageGroup.messages.filter(it => it.createdTo === group._id).map((message, idx) => (
                                    <Draggable
                                      key={message._id}
                                      draggableId={message._id}
                                      index={idx}
                                    >
                                      {(provided_messages2) => (
                                        <div
                                          ref={provided_messages2.innerRef}
                                          {...provided_messages2.draggableProps}
                                          className='draggable-question'
                                        >
                                          <Message
                                            key={message._id}
                                            message={message}
                                            company={company}
                                            group={group}
                                            groupsInfo={messageGroup.groupsInfo}
                                            getMessageGroup={getMessageGroup}
                                            handleDragAndDrop={provided_messages2.dragHandleProps}
                                          />
                                        </div>
                                      )}
                                    </Draggable>
                                  ))
                                }
                                {!snapshot_messages.isDraggingOver && (
                                  <Button
                                    light
                                    icon={<i className="fa fa-plus-circle"></i>}
                                    className='add-message animated fadeIn'
                                    value={I18n.t('dashboard_add-message')}
                                    onClick={()=>addMessage({ mode: 'add', group })}
                                  />
                                )}
                                {provided_messages.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      </div>
                    )
                  }}

                </Draggable>
              ))
            }
            {!snapshot_type?.isDraggingOver && (
              <Button
                light
                icon={<i className="fa fa-plus-circle"></i>}
                className='add-message-type animated fadeIn'
                value={I18n.t('dashboard_add-message-type')}
                onClick={()=>onToggleMessageType()}
              />
            )}
            {provided_type.placeholder}
          </div>
        )}
      </Droppable>
      <MessageType
        isOpen={isOpenMessageType}
        toggle={onToggleMessageType}
        group={messageGroup.groupsInfo.groups.find(it => it._id === groupSelected)}
        getMessageGroup={getMessageGroup}
        groupsInfo={messageGroup.groupsInfo}
      />
      <ModalWrapper
        isOpen={isOpenInfoModal}
        toggle={toggleInfoModal}
        header={' '}
        eventBtnTxt={I18n.t('dashboard_close')}
        eventBtn={toggleInfoModal}
        contentClassName='smartchat-modal'
        body={
          <div className='modal-content'>
            <p>{I18n.t('smartchat_messages_groupInUse')}</p>
          </div>
        }
      />
      <ModalDelete
        isOpen={isOpenDelete}
        toggle={toggleDelete}
        header={I18n.t('dashboard_delete-message-type')}
        text1={I18n.t('chatbot_remove_text1_type_message')}
        text2={I18n.t('chatbot_remove_text2_type_message')}
        event={() => deleteGroup(messageGroup.groupsInfo.groups.find(it => it._id === groupSelected))}
        isSubmitting={isSubmitting}
      />
    </DragDropContext>
  )
}

export default Messages
